import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAppState } from './AppLayout'
import { LandingPage } from './LandingPage'
import { FavoritesBar } from './FavoritesBar'
import { PopularLines } from './PopularLines'
import { NearbyMap, type FavoriteStopPin } from './NearbyMap'
import { useFavorites } from '@/lib/useFavorites'
import { isLandingPage, api } from '@/lib/api'

type HomeTab = 'favourites' | 'lines'

export function HomePage() {
  const { lang, t, city } = useAppState()
  const { favorites } = useFavorites()
  const hasFavorites = favorites.length > 0
  const [activeTab, setActiveTab] = useState<HomeTab>(hasFavorites ? 'favourites' : 'lines')
  const [favoriteStopPins, setFavoriteStopPins] = useState<FavoriteStopPin[]>([])

  // Fetch coordinates for favorite stops
  useEffect(() => {
    if (favorites.length === 0) {
      setFavoriteStopPins([])
      return
    }

    const fetchFavoriteCoordinates = async () => {
      const pins: FavoriteStopPin[] = []
      
      // Fetch stop details for each favorite (dedupe by stopCode)
      const uniqueStopCodes = [...new Set(favorites.map(f => f.stopCode))]
      const stopDetailsMap = new Map<string, { lat: number; lng: number }>()
      
      await Promise.all(
        uniqueStopCodes.map(async (stopCode) => {
          const details = await api.getStopDetails(stopCode)
          if (details?.stop_lat && details?.stop_lng) {
            stopDetailsMap.set(stopCode, {
              lat: parseFloat(details.stop_lat),
              lng: parseFloat(details.stop_lng)
            })
          }
        })
      )

      // Build pins for each favorite
      for (const fav of favorites) {
        const coords = stopDetailsMap.get(fav.stopCode)
        if (coords) {
          pins.push({
            stopCode: fav.stopCode,
            stopName: fav.stopName,
            lat: coords.lat,
            lng: coords.lng,
            lineId: fav.lineId,
            routeCode: fav.routeCode
          })
        }
      }

      setFavoriteStopPins(pins)
    }

    fetchFavoriteCoordinates()
  }, [favorites])

  // If on landing page (no subdomain), show city selection
  if (isLandingPage()) {
    return <LandingPage 
      lang={lang} 
      t={{
        neverMissBus: t.neverMissBus,
        realTimeTracking: t.realTimeTracking,
        selectCity: t.selectCity,
        realTimeETA: t.realTimeETA,
        busLocations: t.busLocations,
        favoriteStops: t.favoriteStops
      }}
    />
  }

  return (
    <div className="home-page">
      {/* Nearby Map - shown at the top of the home page */}
      <div className="home-nearby-section">
        <NearbyMap
          lang={lang}
          translations={{
            loading: t.loading,
            locationError: t.locationError,
            enableLocation: t.enableLocation,
            nearbyStops: t.nearbyStops,
            metersAway: t.metersAway,
            noStopsNearby: t.noStopsNearby
          }}
          favoriteStops={favoriteStopPins}
        />
      </div>
      {city?.id === 'athens' && (
        <Link to="/plan" className="home-plan-link">
          🧭 {lang === 'el' ? 'Σχεδιασμός διαδρομής' : 'Plan a trip'}
        </Link>
      )}
      {/* Tab Navigation - only show if user has favorites */}
      {hasFavorites && (
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'favourites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favourites')}
          >
            {t.favorites}
          </button>
          <button
            className={`tab-button ${activeTab === 'lines' ? 'active' : ''}`}
            onClick={() => setActiveTab('lines')}
          >
            {t.linesTab}
          </button>
        </div>
      )}

      {/* Tab Content */}
      <div className="home-tab-content">
        {hasFavorites && activeTab === 'favourites' && (
          <FavoritesBar lang={lang} />
        )}

        {(!hasFavorites || activeTab === 'lines') && (
          <PopularLines
            cityId={city!.id}
            lang={lang}
            translations={{
              popularLines: t.popularLines,
              allLines: t.allLines
            }}
          />
        )}
      </div>

    </div>
  )
}
